import { memo, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { AlertCircle } from "lucide-react";

import DocumentUploadForm from "../forms/DocumentUploadForm";

import {
  DOCUMENTS_BY_FINANCIER,
  FINANCIER_TYPES,
} from "../constants/documents.config";

import {
  isRequiredDocument,
  shouldShowDocument,
} from "../utils/documentHelpper";
import FileViewDialog from "../../onboarding/details/FileViewDialog";

const DocumentSection = memo(
  ({
    financierType,
    uploadedFiles = {},
    onDocumentUpload,
    onDocumentRemove,
    documentErrors = {},
    context = {},
    readOnly = false,
  }) => {
    const sections = DOCUMENTS_BY_FINANCIER[financierType] || [];

    const [previewState, setPreviewState] = useState({
      open: false,
      file: null,
      fileName: null,
    });

    const handlePreview = useCallback((fileUrl, fileName) => {
      if (!fileUrl) return;
      setPreviewState({ open: true, file: fileUrl, fileName });
    }, []);

    const handleClosePreview = useCallback(() => {
      setPreviewState((prev) => ({ ...prev, open: false }));
    }, []);

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {financierType === FINANCIER_TYPES.AMU && (
          <Box
            sx={{
              p: 2.5,
              backgroundColor: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: "10px",
              display: "flex",
              gap: 2,
            }}
          >
            <AlertCircle
              size={20}
              style={{ color: "#1e40af", flexShrink: 0, marginTop: "2px" }}
            />
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#1e40af",
                  mb: 0.5,
                }}
              >
                Signature Check
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#1e40af" }}>
                System highlights signature fields but does not validate
                content, only presence.
              </Typography>
            </Box>
          </Box>
        )}

        {sections.map((section) => {
          const visibleDocs = (section.documents || []).filter((doc) =>
            shouldShowDocument(doc, context),
          );

          if (visibleDocs.length === 0) return null;

          const IconComponent = section.icon;

          return (
            <Box key={section.id}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 2.5,
                  pb: 2,
                  borderBottom: "2px solid #e5e7eb",
                }}
              >
                {IconComponent && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: "10px",
                      backgroundColor: "#eff6ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <IconComponent size={24} style={{ color: "#2563eb" }} />
                  </Box>
                )}

                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{ fontWeight: 700, fontSize: "18px", color: "#1f2937" }}
                  >
                    {section.category}
                  </Typography>

                  {section.subtext && (
                    <Typography
                      sx={{ fontSize: "12px", color: "#6b7280", mt: 0.25 }}
                    >
                      {section.subtext}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {visibleDocs.map((doc) => {
                  const required = isRequiredDocument(doc, context);

                  return (
                    <Box key={doc.id}>
                      <DocumentUploadForm
                        documentId={doc.id}
                        documentName={doc.name}
                        condition={required ? "Mandatory" : "Optional"}
                        isRequired={required && Boolean(documentErrors[doc.id])}
                        uploadType={doc.uploadType}
                        uploadedFiles={uploadedFiles}
                        onUpload={onDocumentUpload}
                        readOnly={readOnly}
                        onRemove={onDocumentRemove}
                        onPreview={handlePreview}
                      />

                      {documentErrors[doc.id] && (
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#dc2626",
                            mt: 0.75,
                            ml: 1,
                          }}
                        >
                          {documentErrors[doc.id]}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}

        <FileViewDialog
          open={previewState.open}
          onClose={handleClosePreview}
          file={previewState.file}
          fileName={previewState.fileName}
        />
      </Box>
    );
  },
);

DocumentSection.displayName = "DocumentSection";

export default DocumentSection;
